import React, { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ExportPreview from "@/components/ExportPreview";
import { CheckCheck, X, Eye } from "lucide-react";
import { toast } from "sonner";

export const NotaBulkApprove = ({ rows: all, canApprove, onClear, onDone }) => {
  const [open, setOpen] = useState(false);
  const [pv, setPv] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const rows = all.filter((r) => r.status === "Menunggu Persetujuan");
  const total = all.reduce((s, r) => s + (r.total || 0), 0);
  const pdfUrl = `/nota-pengajuan-pdf?ids=${all.map((r) => r.id).join(",")}`;
  const title = all.length === 1 ? `Nota ${all[0].nomor}` : `Nota Belanja (${all.length} nota)`;
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/nota-pengajuan-setujui-massal", { ids: rows.map((x) => x.id), catatan: note });
      const { disetujui, gagal } = r.data;
      if (disetujui.length) toast.success(`${disetujui.length} nota disetujui`);
      if (gagal.length) toast.error(`${gagal.length} nota gagal: ${gagal[0].alasan}`);
      setOpen(false); setNote("");
      window.dispatchEvent(new Event("nota-changed"));
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  if (!all.length) return null;
  return (
    <>
      <div className="sticky bottom-3 z-20 flex flex-wrap items-center gap-3 rounded-xl bg-slate-900 px-4 py-3 text-white shadow-lg" data-testid="nota-bulk-bar">
        <div className="text-sm"><b data-testid="nota-bulk-count">{all.length}</b> nota dipilih · <b className="font-mono" data-testid="nota-bulk-total">{rupiah(total)}</b></div>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="ghost" className="text-slate-300 hover:bg-slate-800 hover:text-white" onClick={onClear} data-testid="nota-bulk-clear"><X className="h-4 w-4 mr-1" /> Batal</Button>
          <Button size="sm" className="bg-orange-600 hover:bg-orange-700" onClick={() => setPv(true)} data-testid="nota-bulk-preview-btn"><Eye className="h-4 w-4 mr-1" /> Preview / PDF / WA</Button>
          {canApprove && rows.length > 0 && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setOpen(true)} data-testid="nota-bulk-approve-btn"><CheckCheck className="h-4 w-4 mr-1" /> Setujui {rows.length} Menunggu</Button>}
        </div>
      </div>
      {pv && <ExportPreview open onClose={() => setPv(false)} title={title} pdfUrl={pdfUrl} items={[{ label: "Unduh PDF", url: pdfUrl, filename: `${title.toLowerCase().replace(/[^a-z0-9]+/g, "_")}.pdf` }]} />}
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent data-testid="nota-bulk-dialog">
          <DialogHeader>
            <DialogTitle>Setujui {rows.length} Nota Sekaligus?</DialogTitle>
            <DialogDescription>Total {rupiah(rows.reduce((s, r) => s + (r.total || 0), 0))}. Persetujuan berarti nota disetujui, bukan bukti pembayaran.</DialogDescription>
          </DialogHeader>
          <div className="max-h-48 overflow-y-auto rounded border text-sm divide-y" data-testid="nota-bulk-list">
            {rows.map((r) => <div key={r.id} className="flex justify-between gap-2 px-3 py-1.5"><span className="font-mono">{r.nomor} · {r.pengaju_nama}</span><span className="font-mono">{rupiah(r.total)}</span></div>)}
          </div>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan pemeriksaan (opsional, berlaku untuk semua)" data-testid="nota-bulk-note" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={busy} onClick={() => setOpen(false)} data-testid="nota-bulk-cancel">Batal</Button>
            <Button disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" onClick={submit} data-testid="nota-bulk-confirm">{busy ? "Memproses…" : "Konfirmasi Setujui"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
